import {Breadcrumbs, Button, Menu, MenuItem, Typography} from "@mui/material";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import {Link as RouterLink} from "react-router";
import React, {useCallback, useId, useState} from "react";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

type Navigate = {
    to: string,
    onClick?: never,
} | {
    to?: never,
    onClick: () => void
};

export type HeaderBreadcrumb = ({
    type: 'link',
    label: string,
} & Navigate) | {
    type: 'text',
    label: string,
} | {
    type: 'menu',
    selectedKey: string,
    label: string,
    options: ({
        key: string,
        label: string,
    } & Navigate)[],
}

HeaderBreadcrumbs.link = function (label: string, to: string | (() => void)): HeaderBreadcrumb & {type: 'link'} {
    return typeof to === 'string' ? {
        type: 'link',
        label,
        to,
    } : {
        type: 'link',
        label,
        onClick: to,
    };
}

HeaderBreadcrumbs.text = function (label: string): HeaderBreadcrumb & {type: 'text'} {
    return {
        type: 'text',
        label,
    };
}

HeaderBreadcrumbs.menu = function <T>(
    selected: T,
    options: readonly T[],
    toOption: (option: T) => {
        key: string,
        label: string,
    } & Navigate,
): HeaderBreadcrumb & {type: 'menu'} {
    const selectedOption = toOption(selected);
    return {
        type: 'menu',
        selectedKey: selectedOption.key,
        label: selectedOption.label,
        options: options.map(toOption),
    };
}

export function HeaderBreadcrumbs({breadcrumbs, variant = 'h4'}:{
    breadcrumbs: HeaderBreadcrumb[],
    variant?: 'h4' | 'h5' | 'h6',
}) {
    return (
        <Breadcrumbs
            aria-label="breadcrumb"
            separator={
                <Typography variant={variant} component="h1">
                    <NavigateNextIcon fontSize="inherit"/>
                </Typography>
            }>
            {breadcrumbs.map((breadcrumb, index) => {
                return breadcrumb.type === 'text' ? (
                    <Typography key={index} variant={variant} component="h1" sx={{pt: '6px'}} gutterBottom>
                        {breadcrumb.label}
                    </Typography>
                ) : breadcrumb.type === 'link' ? (
                    <Button
                        key={index}
                        variant="text"
                        sx={{
                            typography: variant,
                            textTransform: 'none',
                            mb: '0.35em',
                            px: 0,
                        }}
                        {...(breadcrumb.to !== undefined ? {
                            component: RouterLink,
                            to: breadcrumb.to,
                        } : {
                            onClick: breadcrumb.onClick,
                        })}
                    >
                        {breadcrumb.label}
                    </Button>
                ) : breadcrumb.type === 'menu' ? (
                    <MenuHeader key={index} breadcrumb={breadcrumb} variant={variant}/>
                ) : null;
            })}
        </Breadcrumbs>

    );
}

function MenuHeader({breadcrumb, variant}: {
    breadcrumb: HeaderBreadcrumb & {type: 'menu'},
    variant: 'h4' | 'h5' | 'h6',
}) {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const menuId = useId();
    const closeMenu = useCallback(() => setAnchorEl(null), []);

    return (
        <>
            <Button
                variant="text"
                endIcon={<KeyboardArrowDownIcon/>}
                onClick={event => setAnchorEl(event.currentTarget)}
                aria-controls={anchorEl ? menuId : undefined}
                aria-haspopup="menu"
                aria-expanded={Boolean(anchorEl)}
                sx={{
                    typography: variant,
                    textTransform: 'none',
                    mb: '0.35em',
                    px: 0,
                }}
            >
                {breadcrumb.label}
            </Button>
            <Menu id={menuId} anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={closeMenu}>{
                breadcrumb.options.map(option => (
                    <MenuItem
                        key={option.key}
                        selected={breadcrumb.selectedKey === option.key}
                        {...(option.to !== undefined ? {
                            component: RouterLink,
                            to: option.to,
                            onClick: closeMenu,
                        } : {
                            onClick: () => {
                                closeMenu();
                                option.onClick();
                            },
                        })}
                    >
                        {option.label}
                    </MenuItem>
                ))
            }</Menu>
        </>

    );
}
